<div class="hdr">
  <h2 mat-dialog-title><span class="title">{{ title }}</span> Session</h2>
  <div class="mini-close">
    @if (data.action !== 'update') {
      <button matIconButton type="button" mat-dialog-close aria-label="Close session manager.">
        <mat-icon class="material-symbols-outlined icon-sm" fontIcon="close"/>
      </button>
    }
  </div>
</div>

<mat-dialog-content class="mat-typography" #dialogContent>
  <!-- Sensitivity Label -->
  <div class="s-labels-wrapper sticky-row">
      <div class="s-labels-action-button-container">
        @if (slEnabled()) {
          <mat-label style="font-weight: bold">Sensitivity label</mat-label>
        }
        <div class="action-button-container">
          @if (data.action !== 'update') {
            <button matIconButton type="button" matTooltip="Cancel" matTooltipPosition="above" aria-label="Cancel">
              <mat-icon class="material-symbols-outlined" fontIcon="cancel" mat-dialog-close />
            </button>
          }
          <button matIconButton type="button" (click)="onSave()" matTooltip="Save" matTooltipPosition="above" aria-label="Save">
            <mat-icon class="material-symbols-outlined" fontIcon="save" />
          </button>
        </div>
      </div>
    @if (slEnabled()) {
      @defer (when sensitivityLabel.value) {
      <div class="s-label">
        <sensitivity-labels [btnTooltipPosition]="'above'" (labelSelected)="labelSelected($event)" [label]="sensitivityLabel.value" />
        <span>{{sensitivityLabelText}}</span>
      </div>
      }
      @if (!sensitivityLabel.value?.id && submitting) {
      <mat-error>Sensitivity label is required</mat-error>
      }

      <mat-hint class="hint">Selecting a Sensitivity Label for your session can limit which agent and attachments you can use.</mat-hint>
    }
  </div>

  <!-- Session Label -->
  <mat-form-field class="input-field">
    <mat-label>Enter session label</mat-label>
    <input matInput
           [formControl]="sessionLabel"
           (blur)="updateErrorMessage()"
           required />
    @if (sessionLabel.invalid) {
    <mat-error>{{ errorMessage() }}</mat-error>
    }
  </mat-form-field>

  <mat-tab-group
  [(selectedIndex)]="activeTabIndex"
  mat-stretch-tabs="false"
  mat-align-tabs="start"
  animationDuration="0ms">
  <!-- AI Config -->
    <mat-tab label="AI Config">
      <div class="tab-content">
        <!-- Model -->
        <div class="s-labels-wrapper">
          <mat-label style="font-weight: bold">Model</mat-label>
          <div class="s-label">
            <ai-models
              (modelSelected)="modelSelected($event)" [isCreateSessionOrInstruction]="isCreateSession"
              (temperatureChanged)="handleTemperatureChange($event)"
              (topPChanged)="handleTopPChange($event)"
            />
            @if (openAiModel.value?.accountName != '' && openAiModel.value?.model != '') {
              @defer (when openAiModel.value) {
              <span>{{ openAiModel.value?.accountName }}/{{ openAiModel.value?.model }}</span>
              }
            }
          </div>
          <mat-hint class="hint">A model is a program or algorithm that uses incoming data to make informed decisions, predictions, or generate content. </mat-hint>
          @if ((openAiModel.value?.accountName === '' && openAiModel.value?.model === '' || openAiModel.value === null) && submitting) {
            <mat-error>Model is required</mat-error>
            }
        </div>
        <!-- Temperature -->
        <div class="s-labels-wrapper">
          <mat-label style="font-weight: bold">Temperature</mat-label>
          <div>
              <mat-slider min="0.1" max="1" step="0.1" showTickMarks discrete [disabled]="isGpt5Model()">
                  <input matSliderThumb [formControl]="temperatureControl" (valueChange)="handleTemperatureChange($event)">
              </mat-slider>
          </div>
          <mat-hint class="hint">This affects the randomness of the output. Low values like 0.2 make the output more focused and deterministic, while high values (greater than 1.0) produce more diverse and creative outputs.</mat-hint>
        </div>

        <!-- Top P -->
        <div class="s-labels-wrapper">
          <mat-label style="font-weight: bold">Top P</mat-label>
          <div>
              <mat-slider min="0.1" max="1" step="0.1" showTickMarks discrete [disabled]="isGpt5Model()">
                  <input matSliderThumb [formControl]="topPControl" (valueChange)="handleTopPChange($event)">
              </mat-slider>
          </div>
          <mat-hint class="hint">This controls how deterministic the model is. If you are looking for more exact and factual answers, keep the value low. If you are looking for more diverse responses, increase this value.</mat-hint>
        </div>
      </div>
    </mat-tab>
  <!-- Agents -->
    <mat-tab label="Agents">
      <div class="tab-content">
        <div class="disclaimer">
          <strong>What are Agents?</strong>
            <app-info-button-tooltip
              [tooltipText]=agentTooltipText
              [tooltipPosition]="'above'"
            />
          <p>
            Each agent is an independent development from CGI Assist. They have their own teams and support structures in
            place.
          </p>
          <div class="notice">
            <strong>NOTICE</strong>
            @if (slEnabled()) {
              <p>
                Agents have Sensitivity Labels associated to them. Selecting a Sensitivity Label for your session can limit
                which agent and attachments you can use.
                Choose responsibly and follow CGI Federal's security policies.
              </p>
            } @else {
              <p>
                Choose responsibly and follow CGI Federal's security policies.
              </p>
            }
          </div>
        </div>

        <kendo-grid id="session-tbl"
                    class="table-wrapper"
                    [kendoGridBinding]="gridView"
                    [sortable]="true"
                    [resizable]="true"
                    [pageSize]="pageSize"
                    [pageable]="true"
                    [scrollable]="'none'"
                    [(selectedKeys)]="agentSelections"
                    [selectable]="true"
                    kendoGridSelectBy="id"
                    [columnMenu]="{ filter: true }"
                    [rowClass]="rowCallback"
                    [sort]="sort">

          <ng-template kendoGridToolbarTemplate>
            <!--
            Upgrading Kendo UI to v21 caused a focus problem.  The fix is to apply the kendoGridToolbarFocusable directive.
            This directive is only need if the kendoGridExcelCommand, kendoGridAddCommand or kendoGridPDFCommand are used in the toolbar.
            See ticket for more details: https://www.telerik.com/account/support-center/view-ticket/1706516
             -->
            <kendo-textbox
              kendoGridToolbarFocusable (click)="$event.stopPropagation()"
              [style.width.px]="350"
              placeholder="Search in all columns..."
              (valueChange)="onFilter($event)"></kendo-textbox>
            <!-- Info button -->
            <app-info-button-tooltip
              [tooltipText]="'This search bar filters the following data/columns: Name and Description.'"
              [tooltipPosition]="'above'"
            />
            <button kendoButton type="button" #sortButton
                    (click)="sortCheckboxColumn()" [disabled]="this.agentSelections.length === 0" class="btn-sort" [svgIcon]="sortIcon">
              Sort by Selected Agents
            </button>
            <kendo-grid-spacer></kendo-grid-spacer>
            <button kendoGridExcelCommand type="button" [svgIcon]="excelSVG">
              Export to Excel
            </button>
          </ng-template>


          <kendo-grid-checkbox-column field="checkboxField"
                                      [width]="45"
                                      [class]="{ 'text-center': true }"
                                      [resizable]="false"
                                      [columnMenu]="false"
                                      [showSelectAll]="true"></kendo-grid-checkbox-column>

          <kendo-grid-column field="id" title="Id" [hidden]="true"></kendo-grid-column>
          <kendo-grid-column field="name" title="Name"></kendo-grid-column>
          <kendo-grid-column field="description" title="Description"></kendo-grid-column>
          <kendo-grid-column field="sensitivityLabelList" title="Sensitivity Labels" [hidden]="true"></kendo-grid-column>
          <kendo-grid-column field="ownerList" title="ownerList" [hidden]="true"></kendo-grid-column>
          <kendo-grid-column field="supportUrl" title="Support Page" [width]="140">
            <ng-template kendoGridCellTemplate let-dataItem>
              <a [href]="dataItem.supportUrl" target="_blank">
                <mat-icon fontIcon="open_in_new" class="material-symbols-outlined" />
              </a>
            </ng-template>
          </kendo-grid-column>

          <ng-template kendoPagerTemplate
                      let-totalPages="totalPages"
                      let-currentPage="currentPage">
            <kendo-pager-prev-buttons></kendo-pager-prev-buttons>
            <kendo-pager-numeric-buttons [buttonCount]="2"></kendo-pager-numeric-buttons>
            <kendo-pager-next-buttons></kendo-pager-next-buttons>
            <kendo-pager-input></kendo-pager-input>
            <kendo-pager-info></kendo-pager-info>
            <kendo-pager-page-sizes [pageSizes]="pageSizes"></kendo-pager-page-sizes>
          </ng-template>

          <kendo-grid-excel fileName="Session-Agents.xlsx" [filterable]="true">
            <kendo-excelexport-column field="name" [locked]="true" title="Name"></kendo-excelexport-column>
            <kendo-excelexport-column field="description" title="Description"></kendo-excelexport-column>
            <kendo-excelexport-column field="owners" title="Team"></kendo-excelexport-column>
            <kendo-excelexport-column field="supportUrl" title="Support Page"></kendo-excelexport-column>
          </kendo-grid-excel>
        </kendo-grid>
      </div>
    </mat-tab>

  </mat-tab-group>
</mat-dialog-content>
